/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import withStyles from '@material-ui/core/styles/withStyles';

import mbpLogger from 'mbp-logger';
import { useLazyQuery } from '@apollo/react-hooks';
import {
    object, bool, number, func, arrayOf, shape, string,
} from 'prop-types';
import Close from '@material-ui/icons/Close';
import ChevronLeft from '@material-ui/icons/ChevronLeft';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ListItemUI from './GraphqlPDPGiftMessageListItemUI';

import findAllOccasions from '../../../../gql/queries/findAllOccasions';

import { getBrand } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

const styles = (theme) => ({
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: theme.palette.colorPrimary,
        borderBottom: `2px solid ${theme.palette.colorPrimary}`,
        '& h3': {
            margin: '2px 0',
        },
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        outline: 0,
    },
    suggestedMessagesLink: {
        fontSize: '12px',
        color: theme.palette.colorNeutral70,
        textDecoration: 'underline',
        textTransform: 'none',
        display: 'none',
    },
    messageButton: {
        display: 'flex',
        paddingTop: '0px',
        textTransform: 'none',
        textAlign: 'left',
        width: '100%',
    },
    list: {
    },
});

function PanelContainer(props) {
    // eslint-disable-next-line react/prop-types
    const { children } = props;
    return (
        <Typography
            component="div"
            style={{ padding: '0 24px 0 24px' }}
        >
            {children}
        </Typography>
    );
}

const GraphqlOccasionsDialog = ({
    classes, brand, showSuggestedMessages, occasionIndex, activePanel, handleToggleSuggestedMessages, handleSelectOccasion, handleBackToOccasions, handleAddMessage,
}) => {
    const FIND_OCCASIONS_QUERY = findAllOccasions(brand.domain);

    const [
        getOcc, { error, loading, data },
    ] = useLazyQuery(FIND_OCCASIONS_QUERY);

    if (loading) return <div className={classes.suggestedMessagesLink}>...Suggested Messages</div>;

    if (error) {
        mbpLogger.logError({
            query: FIND_OCCASIONS_QUERY,
            component: 'GraphqlOccasionsDialog.js',
            message: 'Error loading data from Graphql',
            error,
        });
        return null;
    }

    const handleClick = () => {
        if (!data)  {
            getOcc();
        }
        handleToggleSuggestedMessages();
    };

    return (
        <>
            <Button
                className={classes.suggestedMessagesLink}
                tabIndex={-1}
                data-test="pdp-suggested-messages"
                data-testid="pdp-suggested-messages" // unit test
                id="suggested-messages-link"
                name="suggestedMessagesLink"
                onClick={handleClick}
            >
                Suggested Messages
            </Button>
            <Dialog
                data-testid="dialogOccasion"
                open={showSuggestedMessages}
                classes={{
                    paperWidthSm: classes.dialogPaperWidth,
                }}
            >
                {/* Occasions Panel */}
                {activePanel === 0 && (
                    <>
                        <DialogTitle
                            disableTypography
                            className={classes.dialogTitle}
                        >
                            <h3>
                                Select an Occasion
                            </h3>
                            <div
                                data-testid="closeDialog"
                                onKeyDown={handleToggleSuggestedMessages}
                                tabIndex={-1}
                                role="button"
                                onClick={handleToggleSuggestedMessages}
                            >
                                <Close />
                            </div>
                        </DialogTitle>
                        <PanelContainer>
                            {data && data.findAllOccasions && data.findAllOccasions.map((occasion) => (
                                <Button
                                    onKeyDown={() => handleSelectOccasion(occasion, occasion.code)}
                                    tabIndex={-1}
                                    role="button"
                                    onClick={() => handleSelectOccasion(occasion, occasion.code)}
                                    key={occasion.name}
                                    className={classes.messageButton}
                                >
                                    <ListItemUI
                                        title={occasion.name}
                                        showCount
                                        count={
                                            occasion.messageText.length
                                        }
                                        value={occasion.messageText}
                                        className={classes.list}
                                    />
                                </Button>
                            ))}
                        </PanelContainer>
                    </>
                )}

                {/* Messages Panel */}
                {activePanel === 1 && (
                    <>
                        <DialogTitle
                            position="sticky"
                            disableTypography
                            className={classes.dialogTitle}
                        >
                            <div
                                className={classes.backButton}
                                onKeyDown={handleBackToOccasions}
                                tabIndex={-1}
                                role="button"
                                onClick={handleBackToOccasions}
                            >
                                <ChevronLeft /> Back
                            </div>
                            <h3>
                                {occasionIndex.name}
                            </h3>
                            <div
                                onKeyDown={handleToggleSuggestedMessages}
                                tabIndex={-1}
                                role="button"
                                onClick={handleToggleSuggestedMessages}
                            >
                                <Close />
                            </div>
                        </DialogTitle>
                        <PanelContainer>
                            {occasionIndex.messageText.map((message) => (
                                <Button
                                    onClick={() => handleAddMessage(message)}
                                    key={message}
                                    className={classes.messageButton}
                                >
                                    <ListItemUI
                                        key={message}
                                        title={message}
                                        className={classes.list}
                                    />
                                </Button>
                            ))}
                        </PanelContainer>
                    </>
                )}
            </Dialog>
        </>
    );
};

GraphqlOccasionsDialog.propTypes = {
    brand: object.isRequired,
    classes: object.isRequired,
    showSuggestedMessages: bool.isRequired,
    activePanel: number.isRequired,
    handleToggleSuggestedMessages: func.isRequired,
    handleSelectOccasion: func.isRequired,
    handleBackToOccasions: func.isRequired,
    handleAddMessage: func.isRequired,
    occasionIndex: shape({
        displayType: string,
        messageText: arrayOf(
            string,
        ),
        name: string,
        rank: string,
        __typename: string,
    }).isRequired,
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
});

export default withRouter(connect(mapStateToProps)(withStyles(styles)(GraphqlOccasionsDialog)));
